import { Link } from "@tanstack/react-router";
import { ComponentType, SVGProps, useState } from "react";
import DownIcon from "assets/img/icons/sidebar/down.svg?react";
import FavoritesIcon from "assets/img/icons/sidebar/favorite-articles.svg?react";
// import { ChevronDownIcon, ChevronUpIcon } from "components/icons/outline";
import FeedIcon from "assets/img/icons/sidebar/feed.svg?react";
import FriendsIcon from "assets/img/icons/sidebar/friends.svg?react";
import InboxIcon from "assets/img/icons/sidebar/inbox.svg?react";
import SocialIcon from "assets/img/icons/sidebar/social.svg?react";
import TrendingIcon from "assets/img/icons/sidebar/trending.svg?react";
import UpIcon from "assets/img/icons/sidebar/up.svg?react";
import UsersIcon from "assets/img/icons/users-icon.svg?react";
import DiscussionIcon from "assets/img/nav-icons/discussion.svg?react";

type SidebarLinkProps = {
    to: string;
    label: string;
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
    isCollapsed: boolean;
};

/**
 * Top-level sidebar link. All entries (Trending, Favorites, Feed, …) share
 * the same hover, active, collapsed-vs-expanded, and icon-spacing rules,
 * so they're rendered through this one component instead of duplicating
 * the styling on every <Link>.
 */
const SidebarLink = ({ to, label, Icon, isCollapsed }: SidebarLinkProps) => (
    <Link
        to={to as "/trending"}
        style={{ textDecoration: "none", color: "inherit" }}
        className={`flex items-center rounded-lg py-[0.20rem] text-gray-700 no-underline hover:bg-brand-shine focus:ring-0 focus:outline-hidden ${
            isCollapsed ? "justify-center px-0" : "px-1"
        }`}
        activeProps={{ className: "bg-brand-shine" }}
    >
        <Icon className={`h-[32px] w-[32px] ${isCollapsed ? "mr-0" : "mr-3"}`} />
        {!isCollapsed && <span>{label}</span>}
    </Link>
);

export const SocialDropdown = ({ isCollapsed }: { isCollapsed: boolean }) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggleDropdown = () => setIsOpen((prev) => !prev);

    const socialLinks = [
        { to: "/messages", name: "Inbox", Icon: InboxIcon, isSub: true },
        { to: "/friends", name: "Friends", Icon: FriendsIcon, isSub: true },
    ];

    return (
        <>
            <SidebarLink
                to="/trending"
                label="Trending"
                Icon={TrendingIcon}
                isCollapsed={isCollapsed}
            />
            <SidebarLink
                to="/favorites/public"
                label="Favorites"
                Icon={FavoritesIcon}
                isCollapsed={isCollapsed}
            />
            <SidebarLink to="/social" label="Feed" Icon={FeedIcon} isCollapsed={isCollapsed} />
            <div>
                <button
                    className={`flex w-full items-center rounded-lg py-1 text-left text-gray-700 hover:bg-brand-shine focus:outline-hidden ${
                        isCollapsed ? "justify-center px-0" : "justify-between px-1"
                    }`}
                    onClick={toggleDropdown}
                >
                    <div className={`flex items-center ${isCollapsed ? "" : "space-x-3"}`}>
                        <SocialIcon className="h-[32px] w-[32px]" />
                        {!isCollapsed && <span>Social</span>}
                    </div>
                    {!isCollapsed && (
                        <span className="text-sm">
                            {isOpen ? (
                                <UpIcon className="mr-1 h-[16px] w-[16px]" />
                            ) : (
                                <DownIcon className="mr-1 h-[16px] w-[16px]" />
                            )}
                        </span>
                    )}
                </button>
                {isOpen && (
                    <ul className={`space-y-0 ${isCollapsed ? "mt-0 ml-0" : "mt-2 ml-0"}`}>
                        {socialLinks.map(({ to, name, Icon }) => (
                            <li
                                key={to}
                                className={
                                    isCollapsed
                                        ? "relative pl-0 before:content-none after:content-none"
                                        : "relative pl-14 before:absolute before:top-1/2 before:left-6 before:h-[2px] before:w-6 before:translate-x-[-4px] before:-translate-y-1/2 before:rounded-r-none before:bg-[#24a2c1] before:shadow-none before:content-[''] after:absolute after:top-0 after:bottom-0 after:left-6 after:w-[2px] after:translate-x-[-4px] after:rounded-l-none after:bg-[#24a2c1] after:content-[''] last:after:h-1/2 last:after:rounded-bl-lg"
                                }
                            >
                                <Link
                                    to={to as "/social"}
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                    className={`block rounded-lg py-[0.20rem] text-gray-700 no-underline hover:bg-brand-shine focus:ring-0 focus:outline-hidden ${
                                        isCollapsed ? "flex justify-center px-0" : "px-[0.20rem]"
                                    }`}
                                    activeProps={{
                                        className: "bg-brand-shine",
                                    }}
                                >
                                    <div
                                        className={`flex items-center ${
                                            isCollapsed ? "justify-center" : "space-x-3"
                                        }`}
                                    >
                                        <Icon className="h-[32px] w-[32px]" />
                                        {!isCollapsed && <div>{name}</div>}
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </>
    );
};
